import { inRange } from 'lodash-es';
import { forwardRef, type KeyboardEventHandler } from 'react';

import Colors from '@dynatrace/strato-design-tokens/colors';

import { BottomMagnifier } from './components/BottomMagnifier.js';
import { TopMagnifier } from './components/TopMagnifier.js';
import type { MagnifierVariant } from './types.js';
import { WithChildren } from '../../../../core/types/with-children.js';
import { usePoliteContainer } from '../../../../overlays/hooks/usePoliteContainer.js';

type VerticalCrosshairProps = {
  /** Coordinate x for crosshair */
  x: number;
  /** Height for crosshair line */
  height: number;
  /** Width for crosshair line */
  width?: number;
  /** Top margin for crosshair line */
  top?: number;
  /** Vertical offset */
  offset?: number;
  /** Indicates wether the line needs to be dashed */
  dashed?: boolean;
  /** Makes the element accessible through keyboard */
  focusable?: boolean;
  /** Chart bounds to calculate magnifier position */
  chartBounds?: DOMRect;
  /** bottom label rotation boolean */
  isBottomLabelRotated?: boolean;
  /** top label rotation boolean */
  isTopLabelRotated?: boolean;
  /** Bottom label for crosshair */
  bottomLabel?: string;
  /** Top label for the crosshair */
  topLabel?: string;
  /** Triggers on key down */
  onKeyDown?: KeyboardEventHandler;
  /** The magnifier variant */
  variant?: MagnifierVariant;
};

export const VerticalCrosshair = forwardRef<
  SVGLineElement,
  VerticalCrosshairProps & WithChildren
>((props: VerticalCrosshairProps, ref) => {
  const {
    x,
    height,
    width = Infinity,
    top = 0,
    offset = 0,
    dashed = true,
    focusable,
    chartBounds,
    isBottomLabelRotated,
    isTopLabelRotated,
    topLabel,
    bottomLabel,
    onKeyDown,
    variant = 'default',
  } = props;
  const Portal = usePoliteContainer();

  const lineStrokeDasharray = dashed ? '3,2' : '0,0';
  const from = { x, y: top };
  const to = { x, y: top + height };
  const strokeWidth = 1;
  const isInRange = inRange(x, 0, width + 1);

  let color = Colors.Background.Field.Neutral.Accent;

  if (variant === 'select') {
    color = Colors.Border.Primary.Accent;
  }

  return (
    <>
      {isInRange && (
        <line
          role="graphics-symbol"
          aria-label="crosshair"
          ref={ref}
          x1={from.x}
          y1={from.y}
          x2={to.x}
          y2={to.y}
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={lineStrokeDasharray}
          shapeRendering="crispEdges"
          pointerEvents="none"
          tabIndex={focusable ? 0 : -1}
          onKeyDown={onKeyDown}
        />
      )}
      {bottomLabel && (
        <Portal>
          <BottomMagnifier
            label={bottomLabel}
            mouseX={x}
            offset={offset}
            color={color}
            chartBounds={chartBounds}
            isRotated={isBottomLabelRotated}
          />
        </Portal>
      )}
      {topLabel && (
        <Portal>
          <TopMagnifier
            label={topLabel}
            mouseX={x}
            offset={offset}
            color={color}
            chartBounds={chartBounds}
            isRotated={isTopLabelRotated}
          />
        </Portal>
      )}
    </>
  );
});

(
  VerticalCrosshair as typeof VerticalCrosshair & { displayName: string }
).displayName = 'VerticalCrosshair';
